import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { REHYDRATE } from "redux-persist";
import type { UnifiedMessage as Message } from "@/types/chat/message.types";
import type { ChatSession, ChatState } from "@/types/index";
import { STORAGE_KEYS } from "@/constants";

const initialState: ChatState = {
  messages: [],
  sessions: [],
  currentSession: null,
  connectionStatus: "disconnected",
  error: null,
  isSending: false,
  selectedDocumentId: null,
  settings: {
    theme: "light",
    notifications: true,
    soundEnabled: true,
  },
  ui: {
    isSidebarOpen: true,
    selectedTab: "chat",
  },
};

/**
 * @critical SLICE DE CHAT COM SINCRONIZAÇÃO WEBSOCKET
 * @description Slice CRÍTICO que gerencia mensagens do chat e sincroniza com WebSocket
 *
 * @purpose EVITA OVERRIDE: Este slice é ESSENCIAL para o funcionamento do painel de perguntas.
 * A função syncWebSocketMessage é OBRIGATÓRIA para sincronização em tempo real.
 *
 * @dependencies
 * - WebSocketContext - Fornece mensagens em tempo real
 * - websocketSyncMiddleware - Middleware que chama syncWebSocketMessage
 * - Chat8PerguntasPanel - Componente que consome as mensagens
 *
 * @warning NÃO REMOVER: syncWebSocketMessage é CRÍTICA para sincronização
 * @warning NÃO MODIFICAR: Lógica de conversão de tipos pode quebrar sincronização
 * @warning NÃO SUBSTITUIR: Outras funções não podem substituir syncWebSocketMessage
 */
export const chatSlice = createSlice({
  name: "chat",
  initialState,
  reducers: {
    setMessages: (state, action: PayloadAction<Message[]>) => {
      state.messages = [...action.payload].sort(
        (a, b) =>
          new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
      );
    },
    addMessage: (state, action: PayloadAction<Message>) => {
      const existingMessage = state.messages.find(
        (m) => m.id === action.payload.id,
      );
      if (!existingMessage) {
        state.messages = [...state.messages, action.payload].sort(
          (a, b) =>
            new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
        );
      }
      // Limpa erro ao adicionar mensagem
      state.error = null;
    },
    setCurrentSession: (state, action: PayloadAction<ChatSession | null>) => {
      // console.log('[setCurrentSession] 🔄 Ação executada:', {
      //   hasPayload: !!action.payload,
      //   payloadId: action.payload?.id,
      //   payloadDocumentId: action.payload?.document_id,
      //   currentSessionId: state.currentSession?.id,
      //   currentSelectedDocumentId: state.selectedDocumentId
      // });
      
      // ✅ ATUALIZAÇÃO FORÇADA: Sempre atualizar currentSession
      state.currentSession = action.payload;
      
      if (action.payload) {
        console.log('[setCurrentSession] ✅ Nova sessão definida:', action.payload.id);
        // Only update selectedDocumentId if it's different or if we don't have one
        const documentId = action.payload.document_id_read || action.payload.document_id;
        if (state.selectedDocumentId !== documentId) {
          console.log('[setCurrentSession] 🔄 Atualizando selectedDocumentId:', state.selectedDocumentId, '→', documentId);
          state.selectedDocumentId = documentId;
          sessionStorage.setItem(
            STORAGE_KEYS.SELECTED_DOCUMENT,
            documentId,
          );
        }
      } else {
        console.log('[setCurrentSession] 🧹 Sessão limpa (null)');
      }
    },
    setConnectionStatus: (
      state,
      action: PayloadAction<ChatState["connectionStatus"]>,
    ) => {
      state.connectionStatus = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    setIsSending: (state, action: PayloadAction<boolean>) => {
      state.isSending = action.payload;
    },
    setSelectedDocumentId: (state, action: PayloadAction<string | null>) => {
      if (!action.payload || action.payload === "undefined") {
        state.selectedDocumentId = null;
        state.currentSession = null;
        sessionStorage.removeItem(STORAGE_KEYS.SELECTED_DOCUMENT);
      } else {
        state.selectedDocumentId = action.payload;
        state.currentSession = null;
        sessionStorage.setItem(STORAGE_KEYS.SELECTED_DOCUMENT, action.payload);
      }
    },
    updateSettings: (
      state,
      action: PayloadAction<Partial<ChatState["settings"]>>,
    ) => {
      state.settings = { ...state.settings, ...action.payload };
    },
    updateUI: (state, action: PayloadAction<Partial<ChatState["ui"]>>) => {
      state.ui = { ...state.ui, ...action.payload };
    },
    clearChat: (state) => {
      state.messages = [];
      state.currentSession = null;
      state.error = null;
      state.isSending = false;
      state.selectedDocumentId = null;
      sessionStorage.removeItem(STORAGE_KEYS.SELECTED_DOCUMENT);
    },
    clearSession: (state) => {
      // Limpar a sessão atual para permitir nova busca
      // Isso é necessário quando o documento muda
      state.messages = [];
      state.currentSession = null;
      state.error = null;
      state.isSending = false;
      // NÃO limpe o selectedDocumentId aqui. A seleção do documento
      // deve persistir mesmo que a sessão de chat seja reiniciada.
      // Esta era a causa do loop de remontagem do WebSocketProvider.
    },
    clearMessages: (state) => {
      state.messages = [];
      state.error = null;
    },
    /**
     * @critical LIMPEZA DE MENSAGENS VAZIAS
     * @description Remove mensagens com conteúdo vazio do estado
     * 
     * @purpose EVITA PROBLEMAS: Remove mensagens corrompidas que podem causar
     * problemas de renderização e performance
     * 
     * @validation: Remove mensagens com content vazio ou apenas espaços
     * @performance: Filtra array em O(n) e atualiza state
     */
    clearEmptyMessages: (state) => {
      state.messages = state.messages.filter(
        (msg) => msg.content && msg.content.trim() !== ""
      );
    },
    /**
     * @critical FUNÇÃO DE SINCRONIZAÇÃO WEBSOCKET-CHAT
     * @description Função CRÍTICA que converte mensagens do WebSocket para o formato do chat
     *
     * @purpose EVITA OVERRIDE: Esta função é OBRIGATÓRIA para sincronização em tempo real.
     * Se removida ou modificada incorretamente, o painel de perguntas não atualizará.
     *
     * @flow CRÍTICO:
     * 1. WebSocket recebe mensagem → websocketSlice atualiza
     * 2. websocketSyncMiddleware detecta mudança → chama syncWebSocketMessage
     * 3. syncWebSocketMessage converte tipos → adiciona ao chatSlice
     * 4. Componentes re-renderizam → Painel de perguntas atualiza
     *
     * @warning NÃO REMOVER: Esta função é ESSENCIAL para sincronização
     * @warning NÃO MODIFICAR: Lógica de conversão de tipos é CRÍTICA
     * @warning NÃO SUBSTITUIR: Outras funções não podem substituir esta funcionalidade
     *
     * @param action.payload - Mensagem do WebSocket (WebSocketMessage format)
     * @returns void - Atualiza state.messages diretamente
     *
     * @conversion CRÍTICA:
     * - WebSocketMessage → Message (chat format)
     * - Timestamp: number → string (ISO format)
     * - Status: WebSocket status → Chat status
     * - Role: sender.type → 'user' | 'assistant'
     *
     * @validation: Converte tipos de forma segura com fallbacks
     * @performance: Adiciona mensagem diretamente ao array (O(1))
     */
    syncWebSocketMessage: (state, action: PayloadAction<any>) => {
      const wsMessage = action.payload;

      // @validation CRÍTICA - Verificar se a mensagem é válida
      if (!wsMessage || typeof wsMessage !== 'object') {
        console.warn('[syncWebSocketMessage] ❌ Mensagem inválida:', wsMessage);
        return;
      }

      // Validação melhorada para diferentes tipos de mensagem
      const messageContent = wsMessage.content || '';
      const isConnectionMessage = wsMessage.type === 'connection_established';
      const isPingPong = wsMessage.type === 'ping' || wsMessage.type === 'pong';
      const isHeartbeat = wsMessage.type === 'heartbeat';
      
      // Não adicionar mensagens de sistema ao chat
      if (isPingPong || isHeartbeat) {
  
        return;
      }
      
      // Mensagens de conexão são processadas internamente, não adicionadas ao chat
      if (isConnectionMessage) {

        return; // Não adicionar ao chat
      }
      
      // Não adicionar mensagens com conteúdo vazio
      if (!messageContent || messageContent.trim() === '') {
        console.warn('[syncWebSocketMessage] ❌ Mensagem com conteúdo vazio ignorada:', {
          id: wsMessage.id,
          type: wsMessage.type,
          content: messageContent,
          reason: 'Empty content detected'
        });
        return;
      }

      const chatMessage: Message = {
        id: wsMessage.id || Date.now().toString(),
        content: messageContent, // Usar conteúdo original
        role: wsMessage.role || (wsMessage.sender?.type === "user" ? "user" : "assistant"),
        timestamp:
          typeof wsMessage.timestamp === "number"
            ? new Date(wsMessage.timestamp).toISOString()
            : wsMessage.timestamp || new Date().toISOString(),
        status: wsMessage.status || "sent",
        session_id: wsMessage.session_id || '',
        document_id: wsMessage.document_id || '',
        created_at: wsMessage.created_at || new Date().toISOString(),
        updated_at: wsMessage.updated_at || new Date().toISOString(),
        sender: wsMessage.sender || {
          id: "assistant",
          type: "assistant",
          name: "Assistente",
        },
        metadata: wsMessage.metadata || {},
      };

      // @validation CRÍTICA - Verificar se a mensagem já existe (evitar duplicatas)
      const existingMessage = state.messages.find((m: Message) => m.id === chatMessage.id);
      if (existingMessage) {
  
        return;
      }

      // @critical ADIÇÃO À LISTA DE MENSAGENS - NÃO MODIFICAR
      // Adiciona mensagem convertida ao array de mensagens do chat
      state.messages.push(chatMessage);
      

    },
  },
  extraReducers: (builder) => {
    builder.addCase(REHYDRATE, (state, action: any) => {
      if (action.payload && action.payload.chat) {
        // Retorna o estado reidratado do persist, preservando os dados salvos
        const rehydratedState = action.payload.chat;

        // Só sobrescreve se o estado reidratado tem dados válidos
        if (
          rehydratedState.currentSession ||
          rehydratedState.selectedDocumentId ||
          rehydratedState.messages?.length > 0
        ) {
          // Aplica o estado reidratado, mas preserva configurações padrão se não existirem
          return {
            ...state,
            ...rehydratedState,
            settings: rehydratedState.settings || state.settings,
            ui: rehydratedState.ui || state.ui,
            connectionStatus:
              rehydratedState.connectionStatus || "disconnected",
            error: rehydratedState.error || null,
            isSending: rehydratedState.isSending || false,
          };
        }
      }

      return state;
    });
  },
});

export const {
  setMessages,
  addMessage,
  setCurrentSession,
  setConnectionStatus,
  setError,
  clearError,
  setIsSending,
  setSelectedDocumentId,
  updateSettings,
  updateUI,
  clearChat,
  clearSession,
  clearMessages,
  clearEmptyMessages,
  syncWebSocketMessage,
} = chatSlice.actions;

// Selectors
export const selectCurrentSession = (state: { chat: ChatState }) =>
  state.chat.currentSession;
export const selectSelectedDocumentId = (state: { chat: ChatState }) =>
  state.chat.selectedDocumentId;

export default chatSlice.reducer;
